import { type ReactNode, useState, useEffect } from "react";
import {
  Layout as RALayout,
  CheckForApplicationUpdate,
  AppBar,
} from "react-admin";
import { Typography, Box, Tabs, Tab, LinearProgress } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { onLoadingChange } from "./loading";

const sections = [
  {
    label: "Cache",
    prefix: "/fdb",
    items: [
      { label: "Overview", path: "/fdb" },
      { label: "Game Configs", path: "/fdb/game-configs" },
    ],
  },
  {
    label: "Records",
    prefix: "/db-",
    items: [
      { label: "Rounds", path: "/db-rounds" },
      { label: "Bets", path: "/db-bets" },
      { label: "Wallet Requests", path: "/db-wallet-requests" },
      { label: "Users", path: "/db-users" },
      { label: "Operators", path: "/db-operators" },
      { label: "Game Configs", path: "/db-game-configs" },
    ],
  },
  {
    label: "Reports",
    prefix: "/operator-stats",
    items: [{ label: "Operator Stats", path: "/operator-stats" }],
  },
  {
    label: "Integrity",
    prefix: "/recon",
    items: [{ label: "Reconciliation", path: "/recon" }],
  },
];

function activeSectionIndex(pathname: string): number {
  for (let i = 0; i < sections.length; i++) {
    if (pathname.startsWith(sections[i].prefix)) return i;
  }
  return 0;
}

function isItemActive(pathname: string, itemPath: string): boolean {
  if (itemPath === "/fdb") return pathname === "/fdb";
  return pathname.startsWith(itemPath);
}

const CustomAppBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const idx = activeSectionIndex(location.pathname);

  return (
    <AppBar
      toolbar={<></>}
      userMenu={false}
      sx={{
        "& .RaAppBar-menuButton": { display: "none" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: "0.75rem", sm: "0.85rem" },
            color: "#e2e8f0",
            letterSpacing: 2,
            textTransform: "uppercase",
            mr: { xs: 2, sm: 5 },
            flexShrink: 0,
          }}
        >
          Aviadrone
        </Typography>
        <Tabs
          value={idx}
          onChange={(_, v) => navigate(sections[v].items[0].path)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 36 }}
        >
          {sections.map((s) => (
            <Tab
              key={s.label}
              label={s.label}
              sx={{ minHeight: 36, py: 0, minWidth: "auto", px: { xs: 1.5, sm: 2 } }}
            />
          ))}
        </Tabs>
      </Box>
    </AppBar>
  );
};

const SubNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const section = sections[activeSectionIndex(location.pathname)];

  if (section.items.length <= 1) return null;

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 9,
        display: "flex",
        gap: 0,
        px: { xs: 1.5, sm: 3 },
        bgcolor: "#0f172a",
        borderBottom: "1px solid #1e293b",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        "&::-webkit-scrollbar": { display: "none" },
        scrollbarWidth: "none",
      }}
    >
      {section.items.map((item) => {
        const active = isItemActive(location.pathname, item.path);
        return (
          <Box
            key={item.path}
            onClick={() => navigate(item.path)}
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: 1,
              cursor: "pointer",
              color: active ? "#c4b5fd" : "#64748b",
              fontSize: { xs: "0.73rem", sm: "0.78rem" },
              fontWeight: active ? 600 : 400,
              borderBottom: active
                ? "2px solid #7c3aed"
                : "2px solid transparent",
              whiteSpace: "nowrap",
              flexShrink: 0,
              "&:hover": { color: "#e2e8f0" },
              transition: "color 0.15s",
            }}
          >
            {item.label}
          </Box>
        );
      })}
    </Box>
  );
};

const EmptyMenu = () => null;

const ContentWrapper = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const unsub = onLoadingChange(setLoading);
    return () => {
      unsub();
    };
  }, []);

  return (
    <Box sx={{ position: "relative", minHeight: "100%" }}>
      {loading && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            height: 2,
          }}
        />
      )}
      <Box
        sx={{
          opacity: loading ? 0.4 : 1,
          pointerEvents: loading ? "none" : "auto",
          transition: "opacity 0.2s",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export const Layout = ({ children }: { children: ReactNode }) => (
  <RALayout
    menu={EmptyMenu}
    appBar={CustomAppBar}
    sx={{
      "& .RaSidebar-root": { display: "none !important" },
      "& .RaSidebar-fixed": { display: "none !important" },
      "& .RaLayout-content": {
        marginLeft: "0 !important",
        maxWidth: "100% !important",
      },
      "& .RaAppBar-menuButton": { display: "none" },
    }}
  >
    <ContentWrapper>
      <SubNav />
      {children}
    </ContentWrapper>
    <CheckForApplicationUpdate />
  </RALayout>
);
